<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'next/icon/Icon.vue';
import Label from 'dashboard/components-next/label/Label.vue';

const props = defineProps({
  title: { type: String, required: true },
  description: { type: String, default: '' },
  icon: { type: String, required: true },
  channelKey: { type: String, default: '' },
  badge: { type: String, default: '' },
  highlights: { type: Array, default: () => [] },
  featured: { type: Boolean, default: false },
  isComingSoon: { type: Boolean, default: false },
  isBeta: { type: Boolean, default: false },
  hasVoiceBadge: { type: Boolean, default: false },
});

const { t } = useI18n();

const channelStyleMap = {
  whatsapp: {
    color: '#3FAE72',
    surface: '#3FAE721A',
  },
  facebook: {
    color: '#4C82B8',
    surface: '#4C82B81A',
  },
  instagram: {
    color: '#B85C73',
    surface: '#B85C731A',
  },
  telegram: {
    color: '#4B91AE',
    surface: '#4B91AE1A',
  },
  api: {
    color: '#A98238',
    surface: '#A982381A',
  },
  website: {
    color: '#7376B8',
    surface: '#7376B81A',
  },
};

const channelStyle = computed(
  () =>
    channelStyleMap[props.channelKey] || {
      color: '#718096',
      surface: '#7180961A',
    }
);
</script>

<template>
  <button
    class="group relative flex w-full cursor-pointer flex-col overflow-hidden rounded-2xl border border-n-slate-5 dark:border-n-slate-7 bg-n-solid-2 p-4 text-left shadow-sm transition-all duration-200"
    :class="[
      featured ? 'min-h-[165px] sm:p-5' : 'min-h-[160px]',
      {
        'hover:enabled:bg-n-alpha-1 hover:enabled:shadow-sm disabled:cursor-not-allowed disabled:opacity-60':
          !isComingSoon,
        'cursor-not-allowed disabled:opacity-80': isComingSoon,
      },
    ]"
  >
    <div class="relative flex w-full items-start justify-between gap-4">
      <div class="relative">
        <div
          class="flex items-center justify-center rounded-2xl border transition-colors duration-200"
          :class="featured ? 'size-12' : 'size-10'"
          :style="{
            backgroundColor: channelStyle.surface,
            borderColor: channelStyle.color,
            color: channelStyle.color,
          }"
        >
          <Icon :icon="icon" :class="featured ? 'size-6' : 'size-5'" />
        </div>
        <div
          v-if="hasVoiceBadge"
          class="absolute -right-1 -top-1 flex size-4 items-center justify-center rounded-full bg-n-alpha-2 ring-2 ring-n-solid-1"
        >
          <Icon icon="i-lucide-audio-lines" class="size-2.5 text-n-slate-10" />
        </div>
      </div>

      <div class="flex items-center gap-2">
        <span
          v-if="badge"
          class="rounded-full border px-2.5 py-1 text-xs font-semibold"
          :style="{
            backgroundColor: channelStyle.surface,
            borderColor: channelStyle.color,
            color: channelStyle.color,
          }"
        >
          {{ badge }}
        </span>
        <span
          class="flex size-9 items-center justify-center rounded-xl bg-n-alpha-2 text-n-slate-10 transition-colors group-hover:bg-n-alpha-3 group-hover:text-n-slate-12"
        >
          <Icon icon="i-lucide-arrow-up-right" class="size-4" />
        </span>
      </div>
    </div>

    <div class="relative mt-4 flex flex-col items-start gap-1.5">
      <div class="flex items-center gap-2">
        <h3
          class="font-semibold text-n-slate-12"
          :class="featured ? 'text-lg' : 'text-base'"
        >
          {{ title }}
        </h3>
        <Label
          v-if="isBeta && !isComingSoon"
          v-tooltip.top="t('GENERAL.BETA_DESCRIPTION')"
          :label="t('GENERAL.BETA')"
          color="blue"
          compact
        />
      </div>
      <p class="max-w-2xl text-sm leading-6 text-n-slate-11">
        {{ description }}
      </p>
      <div v-if="highlights.length" class="mt-2 flex flex-wrap gap-2">
        <span
          v-for="highlight in highlights"
          :key="highlight"
          class="inline-flex items-center gap-1.5 rounded-lg border border-n-weak bg-n-alpha-1 px-2.5 py-1 text-xs font-medium text-n-slate-11"
        >
          <Icon icon="i-lucide-check" class="size-3.5" />
          {{ highlight }}
        </span>
      </div>
    </div>

    <div
      class="relative mt-auto flex items-center gap-2 pt-3 text-xs font-semibold"
      :style="{ color: channelStyle.color }"
    >
      <span>{{ t('CHANNEL_SELECTOR.CONFIGURE') }}</span>
      <Icon icon="i-lucide-arrow-right" class="size-3.5" />
    </div>

    <div
      v-if="isComingSoon"
      class="absolute inset-0 flex cursor-not-allowed items-center justify-center rounded-2xl bg-n-surface-1/85 backdrop-blur-[2px]"
    >
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('CHANNEL_SELECTOR.COMING_SOON') }}
      </span>
    </div>
  </button>
</template>
